import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('admin_email');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  updateSettings: (data) => api.put('/auth/settings', data),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Companies
export const companiesApi = {
  list: (search = '') => api.get('/companies', { params: { search } }),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id, archive = true) => api.delete(`/companies/${id}`, { params: { archive } }),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/companies/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Social Accounts
export const accountsApi = {
  list: (companyId) => api.get(`/companies/${companyId}/accounts`),
  create: (companyId, data) => api.post(`/companies/${companyId}/accounts`, data),
  update: (companyId, accountId, data) => api.put(`/companies/${companyId}/accounts/${accountId}`, data),
  delete: (companyId, accountId) => api.delete(`/companies/${companyId}/accounts/${accountId}`),
};

// Posts
export const postsApi = {
  list: (companyId, params = {}) => api.get(`/companies/${companyId}/posts`, { params }),
  get: (companyId, postId) => api.get(`/companies/${companyId}/posts/${postId}`),
  create: (companyId, data) => api.post(`/companies/${companyId}/posts`, data),
  update: (companyId, postId, data) => api.put(`/companies/${companyId}/posts/${postId}`, data),
  delete: (companyId, postId) => api.delete(`/companies/${companyId}/posts/${postId}`),
  publish: (companyId, postId) => api.post(`/companies/${companyId}/posts/${postId}/publish`),
  duplicate: (companyId, postId) => api.post(`/companies/${companyId}/posts/${postId}/duplicate`),
  calendar: (companyId, month, year) => api.get(`/companies/${companyId}/posts/calendar/view`, { params: { month, year } }),
};

// Media
export const mediaApi = {
  list: (companyId, params = {}) => api.get(`/companies/${companyId}/media`, { params }),
  upload: (companyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/companies/${companyId}/media/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (companyId, mediaId) => api.delete(`/companies/${companyId}/media/${mediaId}`),
};

// Seed
export const seedApi = {
  seed: () => api.post('/seed'),
};

// Integrations
export const integrationsApi = {
  getPlatforms: () => api.get('/integrations/platforms'),
  getPlatformDetail: (platform) => api.get(`/integrations/platforms/${platform}`),
  saveCredentials: (platform, credentials) => api.post(`/integrations/platforms/${platform}/credentials`, credentials),
  deleteCredentials: (platform) => api.delete(`/integrations/platforms/${platform}/credentials`),
  getCallbackUrls: () => api.get('/integrations/callback-urls'),
};

// AI
export const aiApi = {
  generateCaption: (companyId, data) => api.post(`/companies/${companyId}/ai/caption`, data),
  generateImage: (companyId, data) => api.post(`/companies/${companyId}/ai/image`, data, { timeout: 120000 }),
  generateVoice: (companyId, data) => api.post(`/companies/${companyId}/ai/voice`, data),
  generateVideo: (companyId, data) => api.post(`/companies/${companyId}/ai/video`, data, { timeout: 120000 }),
  runPipeline: (companyId, data) => api.post(`/companies/${companyId}/ai/pipeline`, data, { timeout: 180000 }),
};

export default api;
